import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { FaEdit, FaTrash } from "react-icons/fa";
import { DataGrid } from "@mui/x-data-grid";
import { v4 as uuidv4 } from "uuid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { AWS_URL } from "../../Constant";

const getServerProductId = (item) =>
  item?._id || item?.productId || item?.product_id || item?.id || "";

const StoreStocks = ({ storeStoksData, store_id }) => {

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editedProduct, setEditedProduct] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("success");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterProductList, setFilterProductList] = useState([]);
  const [totalStocks, setTotalStocks] = useState([]);

  const resolveItemIdentifier = (row) =>
    row?.serverProductId || getServerProductId(row) || row?.barcode || row?.model_number || "";

  useEffect(() => {
    const products = storeStoksData["Products"] || [];

    const productStocks = products.map((pro, idx) => {
      const serverProductId = getServerProductId(pro);
      return ({
      ...pro,
      rowId: serverProductId || `${pro?.barcode || "product"}-${idx}-${uuidv4()}`,
      serverProductId,
      });
    });
    setTotalStocks(productStocks);
    setFilterProductList(productStocks);
  }, [storeStoksData]);

  const handleDelete = async (row) => {
    const productId = resolveItemIdentifier(row);
    if (!productId) {
      setStatusType("error");
      setStatusMessage("Unable to delete: missing item identifier for this product.");
      return;
    }

    const shouldDelete = window.confirm("Are you sure you want to delete this product?");
    if (!shouldDelete) {
      return;
    }

    try {
      const jwtToken = Cookies.get("jwt_token");
      const encodedStoreId = encodeURIComponent(String(store_id || ""));
      const encodedProductId = encodeURIComponent(String(productId));
      const url = `${AWS_URL}/deleteProduct/${encodedStoreId}/${encodedProductId}`;
      const option = {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      };

      const response = await fetch(url, option);
      let data = {};
      try {
        data = await response.json();
      } catch (error) {
        data = { error: "Invalid server response" };
      }

      if (!response.ok || data?.success === false) {
        setStatusType("error");
        setStatusMessage(data?.error || data?.message || `Delete failed with status ${response.status}`);
        return;
      }

      setTotalStocks((prev) => prev.filter((item) => item.rowId !== row.rowId));
      setStatusType("success");
      setStatusMessage("Product deleted successfully.");
    } catch (error) {
      setStatusType("error");
      setStatusMessage(error?.message || "Delete failed. Please try again.");
    }
  };

  const columns = [
    {
      field: "sno",
      headerName: "ID",
      width: 50,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "productName",
      headerName: "ProductName",
      width: 200,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "brand",
      headerName: "Brands",
      width: 120,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "barcode",
      headerName: "Barcode",
      width: 150,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "model_number",
      headerName: "Model_number",
      width: 150,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "quantity",
      headerName: "Quantity",
      width: 80,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "cutoffprice",
      headerName: "Cutoff_Price",
      width: 110,
      headerClassName: "custom-header",
      headerAlign: "center",
    },

    {
      field: "landingCost",
      headerName: "LandingCost",
      width: 110,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "gst",
      headerName: "GST%",
      type: "number",
      width: 100,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "edit",
      headerName: "Edit",
      headerClassName: "custom-header",
      sortable: false,
      width: 80,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleEdit(params.row)}
        >
          <FaEdit />
        </Button>
      ),
    },

    {
      field: "delete",
      headerName: "Delete",
      headerClassName: "custom-header",

      sortable: false,
      width: 80,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="secondary"
          onClick={() => handleDelete(params.row)}
        >
          <FaTrash />
        </Button>
      ),
    },
  ];

  const handleFilterData = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
  };

  useEffect(() => {
    const regex = new RegExp(searchQuery, "i"); // 'i' flag for case-insensitive matching

    const filterData = totalStocks.filter((product) => {
      return (
        regex.test(product.brand) ||
        regex.test(product.barcode) ||
        regex.test(product.productName)
      );
    });
    setFilterProductList(filterData);
  }, [totalStocks, searchQuery]);

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setEditedProduct({ ...product });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedProduct(null);
    setEditedProduct(null);
    setSaveLoading(false);
  };

  const handleSaveEdit = async () => {
    const serverProductId = resolveItemIdentifier(selectedProduct);

    if (!serverProductId) {
      setStatusType("error");
      setStatusMessage("Unable to edit: missing item identifier for this product.");
      return;
    }

    try {
      setSaveLoading(true);
      const jwtToken = Cookies.get("jwt_token");
      const encodedStoreId = encodeURIComponent(String(store_id || ""));
      const encodedProductId = encodeURIComponent(String(serverProductId));
      const url= `${AWS_URL}/editProduct/${encodedStoreId}/${encodedProductId}`;
      const payload = {
        ...editedProduct,
        productId: serverProductId,
        barcode: editedProduct?.barcode || selectedProduct?.barcode || "",
        model_number: editedProduct?.model_number || selectedProduct?.model_number || "",
      };
      const option = {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify(payload),
      };
      const response = await fetch(url, option);
      if (response.ok) {
        setTotalStocks((prev) =>
          prev.map((item) =>
            item.rowId === selectedProduct.rowId
              ? {
                  ...item,
                  ...editedProduct,
                  serverProductId: item.serverProductId || serverProductId,
                }
              : item
          )
        );
        setStatusType("success");
        setStatusMessage("Product updated successfully.");
        handleCloseEditDialog();
      } else {
        const data = await response.json().catch(() => ({}));
        setStatusType("error");
        setStatusMessage(data?.message || "Update failed on server.");
      }
    } catch (error) {
      setStatusType("error");
      setStatusMessage(error?.message || "Update failed. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setEditedProduct((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const getExportRows = () => {
    return filterProductList.map((item) => ({
      ID: item.sno,
      ProductName: item.productName,
      Brands: item.brand,
      Barcode: item.barcode,
      Model_number: item.model_number,
      Quantity: item.quantity,
      Cutoff_Price: item.cutoffprice,
      LandingCost: item.landingCost,
      "GST%": item.gst,
    }));
  };

  const downloadExcel = () => {
    const rows = getExportRows();
    if (!rows.length) {
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "StoreStocks");
    XLSX.writeFile(workbook, "store-stocks-report.xlsx");
  };

  const downloadPdf = () => {
    const rows = getExportRows();
    if (!rows.length) {
      return;
    }

    const doc = new jsPDF("l", "mm", "a4");
    autoTable(doc, {
      head: [Object.keys(rows[0] || {})],
      body: rows.map((row) => Object.values(row)),
      styles: { fontSize: 8 },
      margin: { top: 15 },
    });
    doc.save("store-stocks-report.pdf");
  };

  return (
    <>
      <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 p-3 shadow-sm">
        <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <h1 className="text-lg font-semibold text-slate-800">State: {storeStoksData.state}</h1>
          <h2 className="text-xl font-bold text-rose-600">Stocks Details</h2>
        </div>

        {statusMessage && (
          <div
            className={`mb-3 rounded-md px-3 py-2 text-sm font-medium ${
              statusType === "error"
                ? "bg-red-100 text-red-700"
                : "bg-green-100 text-green-700"
            }`}
          >
            {statusMessage}
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label htmlFor="admin-store-stocks-search" className="mb-1 text-xs font-semibold text-slate-600">
              Search
            </label>
            <input
              type="search"
              id="admin-store-stocks-search"
              className="h-9 w-64 rounded-md border border-slate-300 bg-white px-2 text-sm placeholder:text-gray-500"
              value={searchQuery}
              onChange={handleFilterData}
              placeholder="Search products"
            />
          </div>

          <div className="ml-auto flex gap-2">
            <button
              className="h-9 rounded-md bg-slate-600 px-3 text-sm font-semibold text-white"
              onClick={handleClearFilters}
            >
              Clear Search
            </button>
            <button
              className="h-9 rounded-md bg-green-600 px-3 text-sm font-semibold text-white"
              onClick={downloadExcel}
            >
              Download Excel
            </button>
            <button
              className="h-9 rounded-md bg-red-600 px-3 text-sm font-semibold text-white"
              onClick={downloadPdf}
            >
              Download PDF
            </button>
          </div>
        </div>
      </div>

      <div style={{ height: 480, width: "100%", overflow: "auto" }}>
        <DataGrid
          sx={{
            boxShadow: 2,
            border: 2,
            borderColor: "#9F2725",
            "& .custom-header": {
              backgroundColor: "#9F2725",
              color: "white",
            },
            "& .MuiDataGrid-colCell,  .MuiDataGrid-cell:hover": {
              color: "primary.main",
            },
            "& .MuiDataGrid-sortIcon ": {
              backgroundColor: "red",
            },
            ".MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
          }}
          rows={filterProductList}
          // rows={products}
          columns={columns}
          getRowId={(row) => row.rowId}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 75 },
            },
          }}
          pageSizeOptions={[5, 15]}
          checkboxSelection
          disableRowSelectionOnClick
        />
      </div>

      <Dialog open={openEditDialog} onClose={handleCloseEditDialog}>
        <DialogTitle>Edit Product</DialogTitle>
        <DialogContent>
          {editedProduct && (
            <>
              <TextField
                label="Product Name"
                value={editedProduct.productName}
                onChange={(e) => handleChange("productName", e.target.value)}
                fullWidth
                margin="dense"
              />
              {/* Add text fields for other fields as needed */}
              <TextField
                label="Brand"
                value={editedProduct.brand}
                onChange={(e) => handleChange("brand", e.target.value)}
                fullWidth
                margin="dense"
              />
              <TextField
                label="Model_number"
                value={editedProduct.model_number}
                onChange={(e) => handleChange("model_number", e.target.value)}
                fullWidth
                margin="dense"
              />
              <TextField
                label="Barcode"
                value={editedProduct.barcode}
                onChange={(e) => handleChange("barcode", e.target.value)}
                fullWidth
                margin="dense"
              />
              <TextField
                label="Quantity"
                value={editedProduct.quantity}
                onChange={(e) => handleChange("quantity", e.target.value)}
                fullWidth
                margin="dense"
                type="number"
              />
              <TextField
                label="landingCost"
                value={editedProduct.landingCost}
                onChange={(e) => handleChange("landingCost", e.target.value)}
                fullWidth
                margin="dense"
                type="number"
              />
              <TextField
                label="cutoffprice"
                value={editedProduct.cutoffprice}
                onChange={(e) => handleChange("cutoffprice", e.target.value)}
                fullWidth
                margin="dense"
                type="number"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSaveEdit} color="primary" disabled={saveLoading}>
            {saveLoading ? "Saving..." : "Save"}
          </Button>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StoreStocks;
